// Réglages : le compte, les notifications Web Push, le brief du matin, les connexions (#/connexions), le module Santé et
// la balance, les appareils (jetons), l'invitation d'un proche, l'apparence (thème et accent). Chaque bloc parle simplement :
// c'est l'écran qu'un proche ouvrira seul.

import { useCallback, useEffect, useState } from 'react';

import type { Appareil, Etat, Moi } from '@alfred/shared';
import type { Actions } from '../App';
import { EnTeteRetour, Fenetre } from '../components';
import { CarteNtfy } from './CarteNtfy';
import { type Connexion, NonConnecte, type ReglagesMcp, api } from '../lib/api';
import { lireFile } from '../lib/fileActions';
import { horsLigneActif, reglerHorsLigne } from '../lib/horsLigne';
import { type EtatAbonnement, activer, desactiver, empechement, lireAbonnement } from '../lib/push';
import { THEMES, type Theme, accentsDu, appliquerApparence, lireAccent, lireTheme } from '../lib/theme';

/** Ce qu'il faut à lib/push pour parler au cœur ; l'installation guidée (Installer.tsx) s'en sert aussi. */
export const DEPENDANCES = { cleServeur: api.cleVapid, declarer: async (s: PushSubscriptionJSON) => void (await api.abonner(s)), retirer: async (e: string) => void (await api.desabonner(e)) };

const EXPLICATIONS: Record<string, string> = {
  'hors-pwa-ios': "Sur iPhone, les notifications n'existent que dans l'app ajoutée à l'écran d'accueil : Partager → Sur l'écran d'accueil, puis rouvrir Alfred depuis l'icône.",
  'sans-push': 'Ce navigateur ne gère pas les notifications.',
  'sans-service-worker': 'Ce navigateur ne gère pas les notifications.',
  refuse: "Les notifications ont été refusées. Il faut les réautoriser dans les réglages du téléphone (Notifications → Alfred).",
};

/**
 * Le lien iCloud du raccourci « Rappel Alfred » (lot 6.4), construit une fois sur un iPhone (docs/RACCOURCI-SIRI.md) :
 * iOS n'importe que des raccourcis signés, le serveur ne peut pas le fabriquer. Null tant qu'il n'existe pas : la partie
 * Siri de Réglages reste alors masquée.
 */
const LIEN_RACCOURCI_SIRI: string | null = null;

/** Les écritures qu'un client MCP peut faire sans validation, si la personne le veut (lot 7.2). */
const OUTILS_DISPENSABLES: Record<string, string> = { rappels_creer: 'Créer un rappel', memoire_retenir: 'Proposer un souvenir (à confirmer ensuite)' };

const ETATS_CONNEXION: Record<Connexion['etat'], string> = { ok: 'connecté', a_reconnecter: 'à reconnecter', erreur: 'en erreur' };

function Titre({ children }: { children: string }) {
  return <span className="display" style={{ fontWeight: 500 }}>{children}</span>;
}

export function Reglages({ moi, actions }: { moi: Moi; actions: Actions }) {
  const [theme, setTheme] = useState<Theme>(lireTheme);
  const [accent, setAccent] = useState(() => lireAccent());
  const [abo, setAbo] = useState<EtatAbonnement>({ actif: false, endpoint: null });
  const [appareils, setAppareils] = useState<Appareil[]>([]);
  const [connexions, setConnexions] = useState<Connexion[]>([]);
  const [etat, setEtat] = useState<Etat | null>(null);
  const [heureBrief, setHeureBrief] = useState('07:00');
  const [heureBilan, setHeureBilan] = useState('22:00');
  const [jetonNeuf, setJetonNeuf] = useState<string | null>(null);
  const [jetonMcp, setJetonMcp] = useState<string | null>(null);
  const [jetonSiri, setJetonSiri] = useState<string | null>(null);
  const [mcp, setMcp] = useState<ReglagesMcp | null>(null);
  const [horsLigne, setHorsLigne] = useState<boolean | null>(null);
  const [lienInvitation, setLienInvitation] = useState<string | null>(null);
  const blocage = empechement();

  const echec = useCallback((e: unknown) => {
    if (e instanceof NonConnecte) window.location.hash = '#/connexion';
    else actions.toast({ texte: (e as Error).message, erreur: true });
  }, [actions]);

  const charger = useCallback(() => {
    void lireAbonnement().then(setAbo).catch(() => undefined);
    api.appareils().then(setAppareils).catch(echec);
    api.connexions().then(setConnexions).catch(echec);
    api.reglagesMcp().then(setMcp).catch(() => undefined);
    void horsLigneActif().then(setHorsLigne);
    if (moi.modules.sante) api.etat().then(setEtat).catch(echec);
  }, [echec, moi.modules.sante]);

  useEffect(charger, [charger]);

  const faire = async (f: () => Promise<unknown>, succes: string) => {
    try {
      await f();
      actions.toast({ texte: succes });
      charger();
    } catch (e) {
      echec(e);
    }
  };

  const basculerPush = async () => {
    try {
      setAbo(abo.actif ? await desactiver(DEPENDANCES) : await activer(DEPENDANCES));
      actions.toast({ texte: abo.actif ? 'Notifications désactivées.' : 'Notifications activées.' });
    } catch (e) {
      const code = (e as Error).message;
      actions.toast({ texte: EXPLICATIONS[code] ?? `Échec : ${code}`, erreur: true });
    }
  };

  const connecterWithings = async () => {
    try {
      window.location.href = (await api.connecterWithings()).url;
    } catch (e) {
      echec(e);
    }
  };

  const deconnecter = async () => {
    const enAttente = (await lireFile()).length;
    if (enAttente && !window.confirm(`${enAttente} action(s) n’ont pas encore pu partir : elles seront perdues. Se déconnecter quand même ?`)) return;
    try {
      await api.deconnexion();
    } finally {
      window.location.hash = '#/connexion';
      window.location.reload();
    }
  };

  return (
    <main className="ecran reglages">
      <EnTeteRetour titre="Réglages" />

      <Fenetre>
        <Titre>Compte</Titre>
        <p style={{ margin: 0 }}>{moi.membre.prenom} · <span className="mono muted">{moi.compte.email}</span></p>
        <p className="muted" style={{ margin: 0, fontSize: 13 }}>Foyer « {moi.foyer.nom} » ({moi.role === 'admin' ? 'administrateur' : 'membre'})</p>
        <button className="btn" onClick={() => void deconnecter()}>Se déconnecter</button>
      </Fenetre>

      <a href="#/assistant" style={{ color: 'inherit' }}>
        <Fenetre>
          <Titre>L’assistant</Titre>
          <p className="muted" style={{ margin: 0, fontSize: 13 }}>Ce qu’Alfred sait de vous, ce qu’il a fait (journal des actions), sa consommation.</p>
        </Fenetre>
      </a>

      <Fenetre>
        <Titre>Notifications</Titre>
        {blocage !== 'aucun' && blocage !== 'refuse' ? (
          <p className="muted" style={{ fontSize: 13, margin: 0 }}>{EXPLICATIONS[blocage]}</p>
        ) : (
          <>
            <p style={{ fontSize: 14, margin: 0 }}>{abo.actif ? 'Actives sur cet appareil.' : 'Inactives sur cet appareil.'}</p>
            <div className="boutons">
              <button className={`btn${abo.actif ? '' : ' principal'}`} onClick={() => void basculerPush()}>{abo.actif ? 'Désactiver' : 'Activer'}</button>
              {abo.actif && <button className="btn" onClick={() => void faire(api.testerPush, 'Notification d’essai envoyée.')}>Tester</button>}
            </div>
          </>
        )}
      </Fenetre>

      <CarteNtfy actions={actions} />

      {/* Le hors-ligne (lot 6.3) : désactivable pour un téléphone partagé. */}
      {horsLigne !== null && (
        <Fenetre>
          <Titre>Hors ligne</Titre>
          <p className="muted" style={{ margin: 0, fontSize: 13, lineHeight: 1.45 }}>
            Alfred garde sur cet appareil la dernière version de la journée, des portails, des rappels et des 20 dernières
            notes ouvertes, pour les lire sans réseau. Tout est effacé quand vous vous déconnectez.
          </p>
          <label className="entretien-case">
            <input type="checkbox" checked={horsLigne} onChange={(e) => {
              const actif = e.target.checked;
              void reglerHorsLigne(actif).then(() => {
                setHorsLigne(actif);
                actions.toast({ texte: actif ? 'Copie hors ligne activée.' : 'Rien n’est plus gardé sur cet appareil.' });
              });
            }} />
            Garder une copie hors ligne sur cet appareil
          </label>
        </Fenetre>
      )}

      <Fenetre>
        <Titre>Brief du matin</Titre>
        <p className="muted" style={{ margin: 0, fontSize: 13 }}>Une notification chaque matin : ce qui vous attend aujourd’hui.</p>
        <div className="boutons">
          <input className="saisie texte" type="time" value={heureBrief} onChange={(e) => setHeureBrief(e.target.value)} aria-label="Heure du brief" style={{ maxWidth: 140 }} />
          <button className="btn principal" onClick={() => void faire(() => api.reglerBrief(true, heureBrief), `Brief chaque jour à ${heureBrief}.`)}>Activer</button>
          <button className="btn" onClick={() => void faire(() => api.reglerBrief(false, heureBrief), 'Brief arrêté.')}>Arrêter</button>
        </div>
      </Fenetre>

      <Fenetre>
        <Titre>Bilan du soir</Titre>
        <p className="muted" style={{ margin: 0, fontSize: 13 }}>Une notification le soir : ce qui a été fait, ce qui reste, et demain.</p>
        <div className="boutons">
          <input className="saisie texte" type="time" value={heureBilan} onChange={(e) => setHeureBilan(e.target.value)} aria-label="Heure du bilan" style={{ maxWidth: 140 }} />
          <button className="btn principal" onClick={() => void faire(() => api.reglerBilan(true, heureBilan), `Bilan chaque soir à ${heureBilan}.`)}>Activer</button>
          <button className="btn" onClick={() => void faire(() => api.reglerBilan(false, heureBilan), 'Bilan arrêté.')}>Arrêter</button>
        </div>
      </Fenetre>

      <Fenetre>
        <Titre>Vos données</Titre>
        <a href="#/notice" style={{ color: 'inherit' }}><p style={{ margin: 0, fontSize: 13 }}>Comment Alfred s’occupe de vos données →<br /><small className="muted">Qui héberge, ce qu’il garde, ce que l’assistant reçoit, vos droits.</small></p></a>
        <a href="#/mes-donnees" style={{ color: 'inherit' }}><p style={{ margin: 0, fontSize: 13 }}>Mes données →<br /><small className="muted">Tout télécharger, ou supprimer mon compte.</small></p></a>
        <a href="#/diagnostic" style={{ color: 'inherit' }}><p style={{ margin: 0, fontSize: 13 }}>Un souci ? →<br /><small className="muted">Envoyer un rapport à l’administrateur, que vous relisez avant.</small></p></a>
      </Fenetre>

      <a href="#/connexions" style={{ color: 'inherit' }}>
        <Fenetre>
          <Titre>Connexions</Titre>
          <p className="muted" style={{ margin: 0, fontSize: 13 }}>
            {connexions.length === 0 ? 'Brancher votre agenda iCloud.' : connexions.map((c) => `${c.nom} · ${ETATS_CONNEXION[c.etat]}`).join(' — ')}
          </p>
        </Fenetre>
      </a>

      {moi.modules.sante ? (
        <Fenetre>
          <Titre>Santé</Titre>
          {connexions.some((c) => c.fournisseur === 'withings')
            ? <p style={{ margin: 0 }}>Balance Withings connectée : la pesée du lundi arrive seule, le soir.</p>
            : <button className="btn" onClick={() => void connecterWithings()}>Connecter la balance Withings</button>}
          {etat && etat.erreurs.length > 0 && (
            <div className="bandeau-erreur">
              <b>À corriger :</b>
              <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>{etat.erreurs.map((x) => <li key={x}>{x}</li>)}</ul>
            </div>
          )}
        </Fenetre>
      ) : (
        <Fenetre>
          <Titre>Santé</Titre>
          <p className="muted" style={{ margin: 0, fontSize: 13 }}>Suivi sportif (séances, marche, pesée) à partir d’un programme dans le cerveau. Réservé à l’installation à la maison.</p>
          <button className="btn" onClick={() => void faire(api.activerSante, 'Module Santé activé.').then(actions.recharger)}>Activer</button>
        </Fenetre>
      )}

      <Fenetre>
        <Titre>Appareils</Titre>
        {appareils.map((a) => (
          <div key={a.id} className="alerte">
            <span className="t">{a.nom}{a.courant ? ' (celui-ci)' : ''}</span>
            <span className="mono muted" style={{ fontSize: 10 }}>{a.dernierVuLe ? new Date(a.dernierVuLe).toLocaleDateString('fr-FR') : ''}</span>
            {!a.courant && <button className="btn petit" onClick={() => void faire(() => api.revoquerAppareil(a.id), 'Appareil retiré.')}>Retirer</button>}
          </div>
        ))}
        <button className="btn" onClick={() => void api.creerAppareil('atelier', 'Passages de Claude Code', ['*']).then((r) => { setJetonNeuf(r.jeton); charger(); }).catch(echec)}>Créer un jeton pour Claude Code</button>
        {jetonNeuf && (
          <p className="bandeau-erreur" style={{ margin: 0 }}>
            Jeton (affiché une seule fois) : <span className="mono" style={{ userSelect: 'all', wordBreak: 'break-all' }}>{jetonNeuf}</span>
          </p>
        )}
      </Fenetre>

      {/* L'ajout rapide (lot 6.4) : un rappel en une phrase, depuis l'icône (Android) ou Siri (iPhone). */}
      <Fenetre>
        <Titre>Ajout rapide</Titre>
        <p className="muted" style={{ margin: 0, fontSize: 13, lineHeight: 1.45 }}>
          Un rappel en une phrase : « appeler le garage demain à 9h ». Dans l’app : <a href="#/rappel-rapide">Nouveau rappel</a>.
          Sur Android, un appui long sur l’icône d’Alfred le propose aussi.
        </p>
        {/* Siri : seulement quand le raccourci existe. Sans lui, l'accès créé ne servirait à rien. */}
        {LIEN_RACCOURCI_SIRI && (
          <>
            <p className="muted" style={{ margin: 0, fontSize: 13, lineHeight: 1.45 }}>
              <b>Avec Siri</b> (« Dis Siri, rappel Alfred ») : créez l’accès, installez le raccourci
              {' '}(<a href={LIEN_RACCOURCI_SIRI} target="_blank" rel="noreferrer">Rappel Alfred</a>),
              puis collez l’adresse et le jeton quand il les demande. Cet accès ne peut que créer des rappels ; il se retire dans « Appareils ».
            </p>
            <button className="btn" onClick={() => void api.creerAppareil('mobile', 'Raccourci Siri', ['rappels']).then((r) => { setJetonSiri(r.jeton); charger(); }).catch(echec)}>
              Créer l’accès pour Siri
            </button>
            {jetonSiri && (
              <>
                <p className="muted" style={{ margin: 0, fontSize: 13 }}>Adresse :</p>
                <p className="mono" style={{ margin: 0, fontSize: 12, userSelect: 'all', wordBreak: 'break-all' }}>{window.location.origin}/api/rappels/rapide</p>
                <p className="muted" style={{ margin: 0, fontSize: 13 }}>Jeton (affiché une seule fois) :</p>
                <p className="bandeau-erreur mono" style={{ margin: 0, userSelect: 'all', wordBreak: 'break-all', fontSize: 12 }}>{jetonSiri}</p>
              </>
            )}
          </>
        )}
      </Fenetre>

      <Fenetre>
        <Titre>Connexions avancées : Claude, ChatGPT (MCP)</Titre>
        <p className="muted" style={{ margin: 0, fontSize: 13 }}>
          Brancher Claude ou ChatGPT sur Alfred : ils lisent votre agenda, vos rappels et votre cerveau, et ce qu’ils veulent
          écrire attend votre accord dans « À valider ». Rien ne se branche sans vous.
        </p>
        {/* La notice « Connexions avancées » (lot 7.4) : ce qui sort de chez Alfred, et comment le reprendre. */}
        <p style={{ margin: 0, fontSize: 13, lineHeight: 1.45 }}>
          <strong>À savoir.</strong> Ce que Claude ou ChatGPT lit dans Alfred part chez Anthropic ou OpenAI, et y est traité
          selon <em>leurs</em> conditions, plus celles d’Alfred. L’assistant d’Alfred, lui, ne part chez personne. Vous coupez
          l’accès quand vous voulez : « Appareils », plus haut, puis « Retirer ».
        </p>
        {mcp && (
          <>
            <p className="muted" style={{ margin: 0, fontSize: 13 }}>
              <b>claude.ai</b> (ordinateur ou application) : Paramètres → Connecteurs → « Ajouter un connecteur personnalisé »,
              collez l’adresse ci-dessous, puis « Se connecter ». Alfred s’ouvre et vous demande ce que vous autorisez.
              {mcp.accordPrive && ' Cette page est sur l’adresse privée d’Alfred : Tailscale doit être actif sur l’appareil, à ce moment-là seulement.'}
            </p>
            <p className="mono" style={{ margin: 0, fontSize: 12, userSelect: 'all', wordBreak: 'break-all' }}>{mcp.adresse}</p>
            <p className="muted" style={{ margin: 0, fontSize: 13 }}>
              <b>ChatGPT</b> : la même adresse, en mode développeur (offres payantes). Pas encore essayé avec Alfred.
            </p>
          </>
        )}
        <p className="muted" style={{ margin: 0, fontSize: 13 }}>
          <b>Claude Code ou Claude Desktop</b>, sur un ordinateur relié à Alfred : un accès par jeton.
        </p>
        <button className="btn" onClick={() => void api.creerAppareil('mcp', 'Claude Code', ['rappels', 'agenda', 'cerveau']).then((r) => { setJetonMcp(r.jeton); charger(); }).catch(echec)}>
          Créer un accès pour Claude Code
        </button>
        {mcp && (
          <>
            <p className="muted" style={{ margin: 0, fontSize: 13 }}>Ce que Claude peut faire sans vous demander (une suppression le demande toujours) :</p>
            {mcp.dispensables.map((outil) => (
              <label key={outil} className="entretien-case">
                <input type="checkbox" checked={mcp.sansValidation.includes(outil)} onChange={(e) => void api.reglerMcp(e.target.checked ? [...mcp.sansValidation, outil] : mcp.sansValidation.filter((x) => x !== outil)).then(setMcp).catch(echec)} />
                {OUTILS_DISPENSABLES[outil] ?? outil}
              </label>
            ))}
          </>
        )}
        {jetonMcp && (
          <>
            <p className="muted" style={{ margin: 0, fontSize: 13 }}>Dans un terminal, une seule fois (la commande contient le jeton, affiché une seule fois) :</p>
            <p className="bandeau-erreur mono" style={{ margin: 0, userSelect: 'all', wordBreak: 'break-all', fontSize: 12 }}>
              claude mcp add --transport http alfred {window.location.origin}/mcp --header "Authorization: Bearer {jetonMcp}"
            </p>
          </>
        )}
      </Fenetre>

      {moi.role === 'admin' && (
        <Fenetre>
          <Titre>Inviter un proche</Titre>
          <p className="muted" style={{ margin: 0, fontSize: 13 }}>Un lien valable 7 jours, à envoyer vous-même (SMS, messagerie). Il ne sert qu’une fois.</p>
          <button className="btn principal" onClick={() => void api.inviter().then((r) => setLienInvitation(r.lien)).catch(echec)}>Créer un lien</button>
          {lienInvitation && <p className="mono" style={{ margin: 0, fontSize: 12, userSelect: 'all', wordBreak: 'break-all' }}>{lienInvitation}</p>}
        </Fenetre>
      )}

      <Fenetre>
        <Titre>Apparence</Titre>
        {/* Nocturne, ou la DA « Système » de la console Brain ; chaque thème garde son propre accent. */}
        <div className="boutons">
          {THEMES.map((t) => (
            <button
              key={t.id}
              className={`btn${theme === t.id ? ' principal' : ''}`}
              aria-pressed={theme === t.id}
              onClick={() => { const a = lireAccent(t.id); appliquerApparence(t.id, a); setTheme(t.id); setAccent(a); }}
            >
              {t.nom}
            </button>
          ))}
        </div>
        <div className="pastilles-accent">
          {accentsDu(theme).map((a) => (
            <button
              key={a.valeur}
              aria-label={a.nom}
              title={a.nom}
              aria-pressed={accent === a.valeur}
              style={{ background: theme === 'systeme' ? a.valeur : `oklch(0.68 0.12 ${a.valeur})` }}
              onClick={() => { appliquerApparence(theme, a.valeur); setAccent(a.valeur); }}
            />
          ))}
        </div>
      </Fenetre>
    </main>
  );
}
